<template>
  <v-container class="pa-20" style="max-width: 1400px !important;">
    <!-- 面包屑导航 -->
    <v-row class="mb-4">
      <v-col cols="12">
        <v-breadcrumbs :items="breadcrumbs" class="pa-0">
          <template v-slot:divider>
            <v-icon>mdi-chevron-right</v-icon>
          </template>
        </v-breadcrumbs>
      </v-col>
    </v-row>

    <!-- 产品详情主体 -->
    <v-row v-if="product">
      <!-- 左侧产品图片 -->
      <v-col cols="12" md="6">
        <v-card class="product-image-card">
          <!-- 主图片 -->
          <div class="main-image-container">
            <v-img
              :src="currentImage"
              :alt="getLocalizedText(product.name)"
              height="500"
              cover
              class="main-image"
              :lazy-src="'/images/products/default/placeholder.jpg'"
              @error="handleImageError"
            >
              <template v-slot:placeholder>
                <v-row class="fill-height ma-0" align="center" justify="center">
                  <v-progress-circular indeterminate color="primary"></v-progress-circular>
                </v-row>
              </template>
              <template v-slot:error>
                <v-row class="fill-height ma-0" align="center" justify="center">
                  <v-icon size="64" color="error">mdi-image-off</v-icon>
                </v-row>
              </template>
            </v-img>
          </div>

          <!-- 缩略图轮播 -->
          <div 
            v-if="productImages.length > 1" 
            class="thumbnail-container"
            ref="thumbnailContainerRef"
          >
            <v-carousel
              v-model="thumbnailCarouselIndex"
              :show-arrows="productImages.length > ITEMS_PER_PAGE ? 'hover' : false"
              height="80"
              hide-delimiter-background
              :continuous="false"
              class="thumbnail-carousel"
              touch
            >
              <v-carousel-item
                v-for="(images, groupIndex) in groupedThumbnails"
                :key="groupIndex"
              >
                <div class="d-flex justify-start thumbnail-wrapper">
                  <div
                    v-for="(image, imageIndex) in images"
                    :key="imageIndex"
                    class="thumbnail-item"
                    :class="{ 
                      'active': currentImageIndex === groupIndex * ITEMS_PER_PAGE + imageIndex,
                    }"
                    @click="selectImage(groupIndex * ITEMS_PER_PAGE + imageIndex)"
                  >
                    <v-img
                      :src="image"
                      :alt="`${getLocalizedText(product.name)} - 图片 ${groupIndex * ITEMS_PER_PAGE + imageIndex + 1}`"
                      :width="thumbnailSize"
                      :height="thumbnailSize"
                      cover
                      class="thumbnail-image"
                      :lazy-src="'/images/products/default/placeholder.jpg'"
                      @error="handleImageError"
                    >
                      <template v-slot:placeholder>
                        <v-icon size="20">mdi-image</v-icon>
                      </template>
                      <template v-slot:error>
                        <v-icon size="20" color="error">mdi-image-off</v-icon>
                      </template>
                    </v-img>
                    <!-- 视频标识 -->
                    <div v-if="groupIndex * ITEMS_PER_PAGE + imageIndex === 0 && product.videos?.length" class="video-overlay">
                      <v-icon color="white" size="20">mdi-play-circle</v-icon>
                    </div>
                  </div>
                </div>
              </v-carousel-item>
            </v-carousel>
          </div>
        </v-card>
      </v-col>

      <!-- 右侧产品信息 -->
      <v-col cols="12" md="6">
        <v-card class="product-info-card">
          <v-card-text class="pa-6">
            <!-- 产品标题 -->
            <h1 class="text-h4 font-weight-bold mb-4">{{ getLocalizedText(product.name) }}</h1>
            
            <!-- 价格信息 -->
            <div class="price-section mb-6">
           <!--    <div class="text-h5 font-weight-bold primary--text mb-2">
                {{ t('products.labels.priceOnRequest') }}
              </div> -->
             
            </div>


            <!-- 简介段落（标题下） -->
            <div v-if="product?.intro || product?.richIntroHtml" class="mb-6">

              <template v-if="product?.richIntroHtml">
                <div class="rich-intro-content" v-html="getLocalizedText(product.richIntroHtml)"></div>
              </template>
              <template v-else>
                <ol class="intro-list">
                  <li v-for="(item, index) in introItems" :key="index" class="text-body-1 mb-2">
                    {{ item }}
                  </li>
                </ol>
              </template>
            </div>

            <!-- 操作按钮 -->
            <div class="action-buttons mb-6">
              <h3 class="text-h6 font-weight-bold mb-3">{{ t('products.labels.standardEquipment') }}</h3>
              <v-row class="action-buttons-row">
                <v-col cols="12" sm="6" class="action-button-col">
                  <v-btn
                      color="error"
                      size="large"
                      block
                      variant="elevated"
                      prepend-icon="mdi-email"
                      @click="inquiryDialog = true"
                      class="action-btn"
                  >
                    <span class="btn-text">{{ t('products.labels.contactSupplier') }}</span>
                  </v-btn>
                </v-col>
                <v-col cols="12" sm="6" class="action-button-col">
                  <v-btn
                      color="primary"
                      size="large"
                      block
                      variant="outlined"
                      prepend-icon="mdi-chat"
                      class="action-btn"
                  >
                    <span class="btn-text">{{ t('products.labels.onlineConsultation') }}</span>
                  </v-btn>
                </v-col>
              </v-row>
            </div>
            <!-- 产品详情 -->
            <div class="product-details mb-6">
              <h3 class="text-h6 font-weight-bold mb-3">{{ t('products.labels.productDetails') }}</h3>
              <v-row>
                <v-col v-for="(info, key) in productInfo" :key="key" cols="6">
                  <div class="detail-item">
                    <span class="text-grey">{{ info.label }}:</span>
                    <span class="ml-2">{{ info.value }}</span>
                  </div>
                </v-col>
              </v-row>
            </div>


          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <!-- 产品描述标签页 -->
    <v-row v-if="product" class="mt-6">
      <v-col cols="12">
        <v-card>
          <v-tabs v-model="activeTab" color="primary" bg-color="grey-lighten-4" hide-slider>
            <v-tab value="description" class="custom-tab">{{ t('products.details.description') }}</v-tab>
            <v-tab value="companyInfo" class="custom-tab">{{ t('products.labels.companyInfo') }}</v-tab>
          </v-tabs>

          <v-window v-model="activeTab">
            <!-- 产品描述 -->
            <v-window-item value="description">
              <v-card-text class="pa-6">
                <!-- 产品描述文本 -->
                <div class="product-description">
                  <template v-if="product?.richDescriptionHtml">
                    <div class="rich-description-content" v-html="getLocalizedText(product.richDescriptionHtml)"></div>
                  </template>
                  <template v-else>
                    <h4 class="text-h6 font-weight-bold mb-3">{{ t('products.labels.basicInfo') }}</h4>
                    <p class="text-body-1">{{ getLocalizedText(product.description) }}</p>
                  </template>
                </div>
                
                <!-- 基本信息表格 -->
                <div v-if="product?.basicInfo" class="basic-info-table mt-6">
                  <h4 class="text-h6 font-weight-bold mb-3">{{ t('products.labels.basicInfo') }}</h4>
                  <v-table>
                    <tbody>
                      <tr>
                        <td class="text-grey" width="120">{{ t('products.labels.modelNo') }}:</td>
                        <td>{{ product.basicInfo.modelNo }}</td>
                        <td class="text-grey" width="120">{{ t('products.labels.certification') }}:</td>
                        <td>{{ product.basicInfo.certification?.join(', ') }}</td>
                      </tr>
                      <tr>
                        <td class="text-grey">{{ t('products.labels.driveType') }}:</td>
                        <td>{{ product.basicInfo.driveType }}</td>
                        <td class="text-grey">{{ t('products.labels.emissionStandard') }}:</td>
                        <td>{{ product.basicInfo.emissionStandard }}</td>
                      </tr>
                      <tr>
                        <td class="text-grey">{{ t('products.labels.fuel') }}:</td>
                        <td>{{ product.basicInfo.fuel }}</td>
                        <td class="text-grey">{{ t('products.labels.serviceLife') }}:</td>
                        <td>{{ product.basicInfo.serviceLife }}</td>
                      </tr>
                      <tr>
                        <td class="text-grey">{{ t('products.labels.size') }}:</td>
                        <td>{{ product.basicInfo.size }}</td>
                        <td class="text-grey">{{ t('products.labels.color') }}:</td>
                        <td>{{ product.basicInfo.color?.join(', ') }}</td>
                      </tr>
                      <tr>
                        <td class="text-grey">{{ t('products.labels.condition') }}:</td>
                        <td>{{ product.basicInfo.condition }}</td>
                        <td class="text-grey">{{ t('products.labels.warranty') }}:</td>
                        <td>{{ product.basicInfo.warranty }}</td>
                      </tr>
                      <tr>
                        <td class="text-grey">{{ t('products.labels.engine') }}:</td>
                        <td>{{ product.basicInfo.engine }}</td>
                        <td class="text-grey">{{ t('products.labels.transmissionType') }}:</td>
                        <td>{{ product.basicInfo.transmissionType }}</td>
                      </tr>
                      <tr>
                        <td class="text-grey">{{ t('products.labels.frontWheel') }}:</td>
                        <td>{{ product.basicInfo.frontWheel }}</td>
                        <td class="text-grey">{{ t('products.labels.farmingTools') }}:</td>
                        <td>{{ product.basicInfo.farmingTools?.join(', ') }}</td>
                      </tr>
                      <tr>
                        <td class="text-grey">{{ t('products.labels.pto') }}:</td>
                        <td>{{ product.basicInfo.pto }}</td>
                        <td class="text-grey">{{ t('products.labels.transportPackage') }}:</td>
                        <td>{{ product.basicInfo.transportPackage }}</td>
                      </tr>
                      <tr>
                        <td class="text-grey">{{ t('products.labels.option') }}:</td>
                        <td>{{ product.basicInfo.options?.join(', ') }}</td>
                        <td class="text-grey">{{ t('products.labels.trademark') }}:</td>
                        <td>{{ product.basicInfo.trademark }}</td>
                      </tr>
                      <tr>
                        <td class="text-grey">{{ t('products.labels.specification') }}:</td>
                        <td>{{ product.basicInfo.specification }}</td>
                        <td class="text-grey">{{ t('products.labels.productionCapacity') }}:</td>
                        <td>{{ product.basicInfo.productionCapacity }}</td>
                      </tr>
                      <tr>
                        <td class="text-grey">{{ t('products.labels.origin') }}:</td>
                        <td>{{ product.basicInfo.origin }}</td>
                        <td></td>
                        <td></td>
                      </tr>
                    </tbody>
                  </v-table>
                </div>

                <!-- 标准配置 -->
                <div v-if="product?.standardEquipment?.length" class="standard-equipment mt-6">
                  <h4 class="text-h6 font-weight-bold mb-3">{{ t('products.labels.standardEquipment') }}</h4>
                  <p>{{ product.standardEquipment.join(', ') }}</p>
                </div>

                <!-- 选配 -->
                <div v-if="product?.basicInfo?.options?.length" class="options mt-4">
                  <h4 class="text-h6 font-weight-bold mb-3">{{ t('products.labels.options') }}</h4>
                  <p>{{ product.basicInfo.options.join(', ') }}</p>
                </div>
              </v-card-text>
            </v-window-item>

            <!-- 公司信息 -->
            <v-window-item value="companyInfo">
              <v-card-text class="pa-6">
                <div class="company-info">
                  <h4 class="text-h6 font-weight-bold mb-4">{{ t('products.labels.companyProfile') }}</h4>
                  <v-row>
                    <v-col cols="12" md="6">
                      <div class="info-item">
                        <span class="text-grey">{{ t('products.labels.companyName') }}:</span>
                        <span class="ml-2">{{ t('products.values.companyName') }}</span>
                      </div>
                      <div class="info-item">
                        <span class="text-grey">{{ t('products.labels.businessType') }}:</span>
                        <span class="ml-2">{{ t('products.values.manufacturer') }}</span>
                      </div>
                      <div class="info-item">
                        <span class="text-grey">{{ t('products.labels.mainProducts') }}:</span>
                        <span class="ml-2">{{ t('products.values.mainProducts') }}</span>
                      </div>
                      <div class="info-item">
                        <span class="text-grey">{{ t('products.labels.employeeCount') }}:</span>
                        <span class="ml-2">500-1000</span>
                      </div>
                    </v-col>
                    <v-col cols="12" md="6">
                      <div class="info-item">
                        <span class="text-grey">{{ t('products.labels.yearEstablished') }}:</span>
                        <span class="ml-2">2000</span>
                      </div>
                      <div class="info-item">
                        <span class="text-grey">{{ t('products.labels.certifications') }}:</span>
                        <span class="ml-2">ISO9001, CE</span>
                      </div>
                      <div class="info-item">
                        <span class="text-grey">{{ t('products.labels.exportMarkets') }}:</span>
                        <span class="ml-2">{{ t('products.values.exportMarkets') }}</span>
                      </div>
                      <div class="info-item">
                        <span class="text-grey">{{ t('products.labels.factorySize') }}:</span>
                        <span class="ml-2">100,000 m²</span>
                      </div>
                    </v-col>
                  </v-row>

                  <h4 class="text-h6 font-weight-bold mt-6 mb-4">{{ t('products.labels.companyAdvantages') }}</h4>
                  <v-row>
                    <v-col cols="12">
                      <div class="advantage-item mb-4">
                        <h5 class="text-subtitle-1 font-weight-bold mb-2">{{ t('products.labels.rdCapability') }}</h5>
                        <p class="text-body-1">{{ t('products.values.rdCapability') }}</p>
                      </div>
                      <div class="advantage-item mb-4">
                        <h5 class="text-subtitle-1 font-weight-bold mb-2">{{ t('products.labels.productionCapability') }}</h5>
                        <p class="text-body-1">{{ t('products.values.productionCapability') }}</p>
                      </div>
                      <div class="advantage-item mb-4">
                        <h5 class="text-subtitle-1 font-weight-bold mb-2">{{ t('products.labels.qualityControl') }}</h5>
                        <p class="text-body-1">{{ t('products.values.qualityControl') }}</p>
                      </div>
                    </v-col>
                  </v-row>
                </div>
              </v-card-text>
            </v-window-item>
          </v-window>
        </v-card>
      </v-col>
    </v-row>

    <!-- 加载状态 -->
    <v-row v-if="loading" justify="center" class="py-12">
      <v-col cols="12" class="text-center">
        <v-progress-circular indeterminate size="64" color="primary" class="mb-4"></v-progress-circular>
        <div class="text-h6 text-grey">正在加载产品详情...</div>
      </v-col>
    </v-row>

    <!-- 错误状态（隐藏UI，仅在控制台记录） -->
    <template v-else-if="error">
      <!-- Intentionally left blank to avoid disrupting the UI -->
    </template>


  </v-container>

  <!-- 联系供应商弹窗 -->
  <v-dialog v-model="inquiryDialog" max-width="900" class="inquiry-dialog">
    <v-card class="inquiry-card">
      <v-card-title class="d-flex justify-space-between align-center dialog-title">
        <span>{{ t('products.labels.contactSupplier') }}</span>
        <v-btn icon="mdi-close" variant="text" @click="inquiryDialog = false"></v-btn>
      </v-card-title>
      <v-card-text class="pa-6 dialog-content">
        <v-row v-if="product" class="mb-4 product-preview-row" align="center">
          <v-col cols="12" md="4" class="product-image-col">
            <v-img
              :src="currentImage"
              :alt="getLocalizedText(product.name)"
              height="160"
              scale-down
              :lazy-src="'/images/products/default/placeholder.jpg'"
              @error="handleImageError"
              class="dialog-product-image"
            />
          </v-col>
          <v-col cols="12" md="8" class="product-info-col">
            <h2 class="text-h5 font-weight-bold mb-4 dialog-product-title">{{ getLocalizedText(product.name) }}</h2>
            <!-- 弹窗中的产品详情（与页面右侧一致） -->
            <div class="product-details mt-2 dialog-product-details">
              <v-row class="dialog-detail-row">
                <v-col v-for="(info, key) in productInfo" :key="key" cols="12" sm="6" class="dialog-detail-col">
                  <div class="detail-item">
                    <span class="text-grey">{{ info.label }}:</span>
                    <span class="ml-2">{{ info.value }}</span>
                  </div>
                </v-col>
              </v-row>
            </div>
          </v-col>
        </v-row>

        <!-- 复用底部联系表单（无卡片样式） -->
        <div class="contact-form-wrapper">
          <ContactForm :bare="true" :showTitle="false" :product_name="getLocalizedText(product.name)" />
        </div>
      </v-card-text>
    </v-card>
  </v-dialog>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { useDisplay } from 'vuetify';
import type { Product, LocalizedString } from '@/types/product';
import {loadProductData, getProductById, refreshProductData} from '@/services/productService';
import ContactForm from '@/components/ContactForm.vue';


const route = useRoute();
const router = useRouter();
const { t, locale } = useI18n();

// 状态
const product = ref<Product | null>(null);
const loading = ref(true);
const error = ref<string | null>(null);
const activeTab = ref('description');
const currentImageIndex = ref(0);
const thumbnailCarouselIndex = ref(0);
const inquiryDialog = ref(false);

const thumbnailContainerRef = ref<HTMLElement | null>(null);
const thumbnailSize = 60; // 缩略图大小
const thumbnailMargin = 4; // 缩略图间距（两边各2px）
const ITEMS_PER_PAGE = 8; // 固定每页8个缩略图

// 图片错误处理
const handleImageError = (_value: string | undefined) => {
  // 图片加载失败时，可以在这里添加错误处理逻辑
  console.warn('图片加载失败:', _value);
};

// 格式化图片路径
const formatImagePath = (path: string) => {
  // 确保路径以斜杠开头
  if (!path.startsWith('/')) {
    path = '/' + path;
  }
  return path;
};

// 将图片数组分组，每组固定8张图片
const groupedThumbnails = computed(() => {
  const formattedImages = productImages.value.map(formatImagePath);
  const groups = [];
  for (let i = 0; i < formattedImages.length; i += ITEMS_PER_PAGE) {
    groups.push(formattedImages.slice(i, i + ITEMS_PER_PAGE));
  }
  return groups;
});

// 监听当前图片索引变化，自动更新轮播索引
watch(currentImageIndex, (newIndex) => {
  thumbnailCarouselIndex.value = Math.floor(newIndex / ITEMS_PER_PAGE);
});

// 监听窗口大小变化
const { name: displayName } = useDisplay();
watch(displayName, () => {
  // updateItemsPerGroup(); // This function is no longer needed
});

// 更新选中的图片
const selectImage = (index: number) => {
  if (index >= 0 && index < productImages.value.length) {
    currentImageIndex.value = index;
  }
};

// 切换到上一张图片
const previousImage = () => {
  if (currentImageIndex.value > 0) {
    currentImageIndex.value--;
  } else {
    currentImageIndex.value = productImages.value.length - 1;
  }
};

// 切换到下一张图片
const nextImage = () => {
  if (currentImageIndex.value < productImages.value.length - 1) {
    currentImageIndex.value++;
  } else {
    currentImageIndex.value = 0;
  }
};

// 默认图片数组
const defaultImages = [

];

// 处理图片路径的辅助函数
const getImagePath = (path: string): string => {
  try {
    // 如果是相对路径，确保以 / 开头
    if (!path.startsWith('/')) {
      return `/${path}`;
  }

  return path;
  } catch (error) {
    console.error('Error processing image path:', error);
    return '/images/products/default/placeholder.jpg';
  }
};

// 获取本地化文本的辅助函数
const getLocalizedText = (localizedString?: LocalizedString | string): string => {
  if (!localizedString) {
    return '';
  }
  if (typeof localizedString === 'string') {
    return localizedString;
  }
  // 根据当前语言返回对应的文本
  return localizedString[locale.value] || localizedString.en || '';
};

// 产品图片数组
const productImages = computed(() => {
  try {
  if (!product.value) return defaultImages;
  
  const images = [];
  
  // 如果有产品图片，使用产品图片
  if (product.value.imageUrl) {
    images.push(getImagePath(product.value.imageUrl));
  }
    console.log('Product images:', product.value.productImages)
    // 如果有产品图片数组，添加它们
    if (product.value.productImages?.length) {
      images.push(...product.value.productImages.map(getImagePath));
  }
  
  // 如果有视频，添加视频缩略图
  if (product.value.videos?.length) {
    images.unshift('/images/video-thumbnail.jpg');
  }
  
  // 如果没有任何图片，使用默认图片
  if (images.length === 0) {
    return defaultImages;
  }
  
    console.log('Product images:', images);
  return images;
  } catch (error) {
    console.error('Error processing product images:', error);
    return defaultImages;
  }
});

// 当前显示的图片
const currentImage = computed(() => {
  const path = productImages.value[currentImageIndex.value];
  return path ? formatImagePath(path) : '/images/products/default/placeholder.jpg';
});

// 面包屑导航
const breadcrumbs = computed(() => [
  { 
    title: t('nav.home'),
    to: '/' 
  },
  { 
    title: t('nav.products'),
    to: '/products' 
  },

  { 
    title: product.value ? getLocalizedText(product.value.name) : t('products.labels.productDetails'),
    disabled: true 
  }
]);

// 产品详情信息
const productInfo = computed(() => ({
  customization: {
    label: t('products.labels.customization'),
    value: t('products.values.customizationSupported')
  },
  afterSalesService: {
    label: t('products.labels.afterSalesService'),
    value: t('products.values.afterSalesService')
  },
  warranty: {
    label: t('products.labels.warranty'),
    value: '18 ' + t('products.labels.months')
  },
  deliveryTime: {
    label: t('products.labels.deliveryTime'),
    value: '10-45 ' + t('products.labels.days')
  }
}));

// 解析 intro 为列表项
const introItems = computed(() => {
  if (!product.value?.intro) return [];
  
  const introText = getLocalizedText(product.value.intro);
  
  // 尝试按数字序号分割（如 "1. xxx, 2. xxx, 3. xxx"）
  const splitByNumbers = introText.split(/,?\s*\d+\.\s+/).filter(item => item.trim());
  
  if (splitByNumbers.length > 1) {
    return splitByNumbers.map(item => item.trim().replace(/,$/, ''));
  }
  
  // 如果没有数字序号，尝试按逗号分割
  const splitByComma = introText.split(',').filter(item => item.trim());
  
  if (splitByComma.length > 1) {
    return splitByComma.map(item => item.trim());
  }
  
  // 如果都不行，返回整个文本作为单个项
  return [introText];
});

// 加载产品数据
const loadProduct = async () => {
  const productId = route.params.id as string;
  if (!productId) {
    error.value = '产品ID不存在';
    loading.value = false;
    return;
  }

  loading.value = true;
  error.value = null;

  try {
    // 1) Load fast (may come from cache)
    const cachedCategories = await loadProductData();
    let foundProduct = getProductById(cachedCategories, productId);
    
    if (foundProduct) {
      product.value = foundProduct;
    } else {
      error.value = '产品不存在';
    }
    console.log('Found product:', foundProduct);

    // 2) Refresh in background and update if product changed
    refreshProductData()
      .then((freshCategories) => {
        const refreshed = getProductById(freshCategories, productId);
        if (refreshed) {
          product.value = refreshed;
        }
      })
      .catch((err) => console.warn('Background refresh (detail) failed:', err));
  } catch (err) {
    console.error('加载产品详情失败:', err);
    error.value = err instanceof Error ? err.message : '加载产品详情失败';
  } finally {
    loading.value = false;
  }
};

onMounted(() => {
  // updateItemsPerGroup(); // This function is no longer needed
  // 创建 ResizeObserver 监听容器大小变化
  const observer = new ResizeObserver(() => {
    // updateItemsPerGroup(); // This function is no longer needed
  });
  
  if (thumbnailContainerRef.value) {
    observer.observe(thumbnailContainerRef.value);
  }
  loadProduct();
});
</script>

<style scoped>
.product-image-card {
  border-radius: 8px;
  overflow: hidden;
}

.main-image-container {
  position: relative;
  height: 500px;
}

.main-image {
  transition: transform 0.3s ease;
}

.nav-arrow {
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  z-index: 1;
}

.nav-arrow-left {
  left: 8px;
}

.nav-arrow-right {
  right: 8px;
}

.thumbnail-container {
  margin-top: 16px;
  width: 100%;
  position: relative;
  overflow: hidden;
  max-width: 100%;
}

.thumbnail-carousel {
  background-color: transparent;
  padding: 0 32px;
  width: 100%;
  max-width: 100%;
}

.thumbnail-carousel :deep(.v-carousel__controls) {
  display: none;
}

.thumbnail-carousel :deep(.v-window__container) {
  max-width: 100%;
  overflow: visible;
}

.thumbnail-carousel :deep(.v-carousel__item) {
  max-width: 100%;
}

.thumbnail-wrapper {
  padding: 4px 0;
  gap: 8px;
  width: 100%;
  max-width: 100%;
  flex-wrap: nowrap;
}

.thumbnail-item {
  cursor: pointer;
  position: relative;
  border: 2px solid transparent;
  border-radius: 4px;
  transition: all 0.3s ease;
  flex: 0 0 auto;
  overflow: hidden;
  width: 60px;
  height: 60px;
}

.thumbnail-item:hover {
  border-color: var(--v-primary-base);
  opacity: 0.9;
}

.thumbnail-item.active {
  border-color: var(--v-primary-base);
}

.thumbnail-image {
  border-radius: 4px;
  background-color: #f5f5f5;
  width: 100%;
  height: 100%;
}

.video-overlay {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  background-color: rgba(0, 0, 0, 0.5);
  border-radius: 50%;
  padding: 2px;
}

/* 自定义轮播箭头样式 */
.thumbnail-carousel :deep(.v-carousel__prev),
.thumbnail-carousel :deep(.v-carousel__next) {
  position: absolute;
  background-color: rgba(0, 0, 0, 0.4) !important;
  width: 32px !important;
  height: 72px !important;
  top: 50%;
  transform: translateY(-50%);
  margin: 0;
  border-radius: 4px;
  z-index: 10;
  opacity: 1 !important;
}

.thumbnail-carousel :deep(.v-carousel__prev) {
  left: 0;
}

.thumbnail-carousel :deep(.v-carousel__next) {
  right: 0;
}

.thumbnail-carousel :deep(.v-carousel__prev .v-btn__content),
.thumbnail-carousel :deep(.v-carousel__next .v-btn__content) {
  color: white !important;
  opacity: 1 !important;
}

.thumbnail-carousel :deep(.v-carousel__prev .v-icon),
.thumbnail-carousel :deep(.v-carousel__next .v-icon) {
  color: white !important;
  font-size: 24px !important;
}

.thumbnail-carousel :deep(.v-carousel__prev:hover),
.thumbnail-carousel :deep(.v-carousel__next:hover) {
  background-color: rgba(0, 0, 0, 0.6) !important;
}

.thumbnail-carousel :deep(.v-carousel__prev:hover .v-btn__content),
.thumbnail-carousel :deep(.v-carousel__next:hover .v-btn__content) {
  opacity: 1;
}

.thumbnail-carousel :deep(.v-window__container) {
  height: 80px !important;
  max-width: 100%;
}

/* 防止页面横向滚动 */
.product-image-card {
  overflow: hidden;
  max-width: 100%;
}

.main-image-container {
  overflow: hidden;
  max-width: 100%;
}

.product-info-card {
  height: 100%;
  border-radius: 8px;
}

.detail-item {
  display: flex;
  align-items: center;
}

.info-item, .spec-item {
  display: flex;
  align-items: center;
  margin-bottom: 4px;
  padding: 8px 0;
}



/* Rich Description 表格样式 */
.rich-description-content {
  width: 100%;
}

.rich-description-content :deep(h4) {
  font-size: 1.125rem;
  font-weight: 600;
  margin-top: 24px;
  margin-bottom: 16px;
  color: #1a1a1a;
}

.rich-description-content :deep(h4:first-child) {
  margin-top: 0;
}

.rich-description-content :deep(table) {
  width: 100%;
  border-collapse: collapse;
  margin-bottom: 24px;
  border: 1px solid #e0e0e0;
  background-color: #fff;
}

.rich-description-content :deep(table th),
.rich-description-content :deep(table td) {
  padding: 12px 16px;
  text-align: left;
  border: 1px solid #e0e0e0;
  font-size: 0.9rem;
  line-height: 1.5;
}

.rich-description-content :deep(table th) {
  background-color: #f5f5f5;
  font-weight: 600;
  color: #333;
}

.rich-description-content :deep(table tr:nth-child(even)) {
  background-color: #fafafa;
}

.rich-description-content :deep(table tr:hover) {
  background-color: #f0f0f0;
}

/* Rich Intro 内容样式 */
.rich-intro-content {
  width: 100%;
  color: #333;
  font-size: 15px;
  line-height: 1.7;
}

.rich-intro-content :deep(p) {
  margin: 8px 0 12px;
  color: #444;
}

.rich-intro-content :deep(ul),
.rich-intro-content :deep(ol) {
  padding-left: 24px;
  margin: 8px 0 12px;
  line-height: 1.8;
}

.rich-intro-content :deep(li) {
  margin-bottom: 8px;
  color: #333;
}

.rich-intro-content :deep(table) {
  width: 100%;
  border-collapse: collapse;
  margin: 12px 0;
  border: 1px solid #e0e0e0;
  background-color: #fff;
}

.rich-intro-content :deep(table th),
.rich-intro-content :deep(table td) {
  padding: 10px 14px;
  text-align: left;
  border: 1px solid #e0e0e0;
  font-size: 0.875rem;
  line-height: 1.5;
}

.rich-intro-content :deep(table th) {
  background-color: #f5f5f5;
  font-weight: 600;
  color: #333;
}

.rich-intro-content :deep(table tr:nth-child(even)) {
  background-color: #fafafa;
}

.rich-intro-content :deep(table tr:hover) {
  background-color: #f0f7ff;
}

/* 自定义tab样式 */
.custom-tab {
  font-weight: 600 !important;
  text-transform: none !important;
  letter-spacing: normal !important;
  min-width: 240px !important;
  padding: 0 24px !important;
  height: 48px !important;
  display: flex !important;
  align-items: center !important;
  justify-content: center !important;
}

.custom-tab.v-tab--selected {
  background-color: white !important;
  color: rgb(var(--v-theme-primary)) !important;
  font-weight: 700 !important;
  border-top: 3px solid rgb(var(--v-theme-primary)) !important;
  border-bottom: none !important;
}

.custom-tab:not(.v-tab--selected) {
  background-color: #f5f5f5 !important;
  color: #666 !important;
}

/* 移动端按钮样式优化 */
.action-buttons-row {
  margin: 0 -12px;
}

.action-button-col {
  padding: 6px 12px;
}

.action-btn {
  white-space: nowrap;
}

.action-btn .btn-text {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

/* 弹窗样式优化 */
.inquiry-dialog .dialog-title {
  padding: 20px 24px;
  border-bottom: 1px solid #e0e0e0;
}

.inquiry-dialog .dialog-content {
  overflow-y: auto;
  max-height: 80vh;
}

.product-preview-row {
  margin-bottom: 24px;
}

.dialog-product-title {
  font-size: 1.25rem;
  line-height: 1.4;
}

.dialog-product-details {
  margin-top: 12px;
}

.dialog-detail-row {
  margin: 0 -8px;
}

.dialog-detail-col {
  padding: 4px 8px;
}

.contact-form-wrapper {
  margin-top: 24px;
}

/* 响应式调整 */
@media (max-width: 600px) {
  .custom-tab {
    min-width: 160px !important;
    padding: 0 16px !important;
    font-size: 14px !important;
  }

  /* 移动端按钮优化 */
  .action-button-col {
    padding: 6px 8px;
  }

  .action-btn {
    font-size: 0.85rem !important;
    padding: 0 8px !important;
    min-width: auto !important;
  }

  .action-btn .v-icon {
    font-size: 18px !important;
  }

  .action-btn .btn-text {
    font-size: 0.8rem;
  }

  /* 弹窗移动端优化 */
  .inquiry-dialog .v-card {
    margin: 12px;
    max-height: 95vh;
  }

  .inquiry-dialog .dialog-content {
    padding: 16px !important;
    max-height: 75vh;
  }

  .product-preview-row {
    margin-bottom: 16px;
  }

  .product-image-col {
    margin-bottom: 12px;
  }

  .dialog-product-image {
    height: 120px !important;
  }

  .dialog-product-title {
    font-size: 1.1rem !important;
    margin-bottom: 12px !important;
  }

  .dialog-product-details {
    margin-top: 8px;
  }

  .dialog-detail-col {
    padding: 4px 6px;
    font-size: 0.85rem;
  }

  .detail-item {
    flex-direction: column;
    align-items: flex-start;
    margin-bottom: 8px;
  }

  .detail-item .text-grey {
    font-size: 0.8rem;
    margin-bottom: 2px;
  }

  .detail-item .ml-2 {
    margin-left: 0 !important;
    font-size: 0.85rem;
  }

  .contact-form-wrapper {
    margin-top: 16px;
  }
}

/* 滚动条样式 */
.thumbnail-scroll::-webkit-scrollbar {
  height: 4px;
}

.thumbnail-scroll::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 2px;
}

.thumbnail-scroll::-webkit-scrollbar-thumb {
  background: #c1c1c1;
  border-radius: 2px;
}

.thumbnail-scroll::-webkit-scrollbar-thumb:hover {
  background: #a8a8a8;
}

/* 表格样式 */
.v-table {
  border: 1px solid #e0e0e0;
}

.v-table td {
  padding: 12px 16px;
  border-bottom: 1px solid #e0e0e0;
  border-right: 1px solid #e0e0e0;
}

.v-table tr td:last-child {
  border-right: none;
}

.v-table tr:last-child td {
  border-bottom: none;
}

.text-grey {
  color: #666;
  font-weight: 500;
}

/* 标题样式 */
.text-subtitle-1 {
  background-color: #f5f5f5;
  padding: 12px 16px;
}

/* 列表样式 */
ul {
  list-style-type: disc;
  margin: 0;
  padding-left: 20px;
}

li {
  margin-bottom: 8px;
}

/* intro 列表样式 */
.intro-list {
  list-style-type: decimal;
  margin: 0;
  padding-left: 24px;
  line-height: 1.8;
}

.intro-list li {
  margin-bottom: 12px;
  color: #333;
}

/* 表格组标题 */
.v-table tr td[colspan="2"] {
  background-color: #f5f5f5;
  font-weight: bold;
  border-bottom: 2px solid #e0e0e0;
}

/* 表格间距 */
.v-table + .v-table {
  margin-top: 24px;
  border-top: 2px solid #e0e0e0;
}

/* Added styles for rich description content */
.product-description {
  overflow-x: auto;
}
:deep(.product-description table) {
  width: 100%;
  border-collapse: separate;
  border-spacing: 0;
  background: #fff;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.06);
}
:deep(.product-description th),
:deep(.product-description td) {
  border-right: 1px solid #eeeeee;
  border-bottom: 1px solid #eeeeee;
  padding: 10px 12px;
  vertical-align: top;
  line-height: 1.5;
}
:deep(.product-description th) {
  background: #f5f5f7;
  color: #555;
  font-weight: 600;
  text-align: left;
}
:deep(.product-description tr:nth-child(even) td) {
  background: #fafafa;
}
:deep(.product-description tr:hover td) {
  background: #f0f7ff;
}
:deep(.product-description tr:last-child td) {
  border-bottom: 0;
}
:deep(.product-description th:last-child),
:deep(.product-description td:last-child) {
  border-right: 0;
}
.product-description h3,
.product-description h4 {
  margin-top: 16px;
}

/* Beautify non-table rich text inside v-html */
:deep(.product-description) {
  color: #333;
  font-size: 16px;
  line-height: 1.7;
}
:deep(.product-description p) {
  margin: 8px 0 14px;
  color: #444;
}
:deep(.product-description h3) {
  font-size: 20px;
  margin: 12px 0;
  color: #222;
}
:deep(.product-description h4) {
  font-size: 18px;
  margin: 14px 0 8px;
  color: #222;
}
:deep(.product-description ul) {
  padding-left: 22px;
  margin: 8px 0 14px;
}
:deep(.product-description li) {
  margin-bottom: 6px;
}
:deep(.product-description table) {
  margin: 12px 0 18px;
}
</style> 